import { useEffect } from "react";
import { type ShouldRevalidateFunction, useLocation } from "react-router";

import type { ProductHitWithSwatches } from "~/lib/app.types";
import { isAuthRevalidatingSubmit } from "~/routes/auth-revalidate";

export const CAT_PARAM = "cat";
export const GRID_SIZE = 24;

/** One chip's products, as the grid renders them. */
export interface SpenderListing {
  hits: ProductHitWithSwatches[];
  total: number;
  /** CIO `result_id`, for the browse beacons. */
  resultId?: string;
}
/** A failed listing: the grid shows its empty message. */
export const EMPTY_LISTING: SpenderListing = { hits: [], total: 0 };
/** COM-474: the Builder marker block. Any FSA Builder page that has it is the spender page (COM-883). */
export const SPENDER_BLOCK_NAME = "FsaSpenderPage";
/** The chip-row block; the server reads its authored `chips` before render. */
export const SPENDER_CHIP_ROW_BLOCK_NAME = "SpenderChipRow";
/**
 * COM-893: the `categoryId` a `plp-inline-content-setup` entry targets to
 * place tiles in the spender grid. No real category has it, so such an entry
 * never shows on a PLP, and the grid shows only this entry, on every chip.
 */
export const SPENDER_INLINE_CATEGORY_ID = "fsastore-spender";

// COM-883: the path the spender page is mounted on in this browser.
// `shouldRevalidate` sees only URLs, so the page records where it lives. Set in
// an effect, so the server never holds it across requests.
let mountedSpenderPath: string | null = null;

export const isSpenderPageMounted = (pathname: string): boolean =>
  mountedSpenderPath === pathname;

export function useSpenderPageMount(active: boolean): void {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!active) return;
    mountedSpenderPath = pathname;
    return () => {
      if (mountedSpenderPath === pathname) mountedSpenderPath = null;
    };
  }, [active, pathname]);
}

/** The URL params the grid's listing is read from: the chip, and the PLP's `page`. */
const LISTING_PARAMS = [CAT_PARAM, "page"];

// The loader re-runs for sign-in/out and for a new chip or page; basket POSTs
// and the other params (view options) never re-run it.
export const spenderShouldRevalidate: ShouldRevalidateFunction = args => {
  const { currentUrl, nextUrl, formMethod, defaultShouldRevalidate } = args;
  if (formMethod && formMethod !== "GET") {
    return isAuthRevalidatingSubmit(args);
  }
  if (
    currentUrl.pathname !== nextUrl.pathname ||
    currentUrl.href === nextUrl.href
  ) {
    return defaultShouldRevalidate;
  }
  return LISTING_PARAMS.some(
    key => currentUrl.searchParams.get(key) !== nextUrl.searchParams.get(key),
  );
};
